import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, ReplyAll, CalendarCheck, Clock, BarChart3,
  TrendingUp, ArrowRight, Play, Check, ChevronDown, ChevronUp,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export const HUB_CAPABILITIES = [
  { key: "connect",   Icon: MessageSquare,
    title: "Connect",   line: "Every website form and widget chat lands in one inbox." },
  { key: "respond",   Icon: ReplyAll,
    title: "Respond",   line: "Reply on any channel with an AI-drafted first reply." },
  { key: "book",      Icon: CalendarCheck,
    title: "Book",      line: "Turn a conversation into a booked appointment in one click." },
  { key: "follow_up", Icon: Clock,
    title: "Follow up", line: "Automatic 4-hour owner nudges so no enquiry goes cold." },
  { key: "understand",Icon: BarChart3,
    title: "Understand",line: "Timeline, source, outcome — one contact record, forever." },
  { key: "grow",      Icon: TrendingUp,
    title: "Grow",      line: "Weekly outcome summary shows what's converting and what's not." },
];

const FAQS = [
  { q: "Do I need to switch email, phone or CRM providers?",
    a: "No. The Conversation Hub sits on top of what you already have. It plugs into your website first, then optionally connects to your existing business email, SMS, and calling providers." },
  { q: "How is this different from a chatbot?",
    a: "A chatbot answers on your website. The Conversation Hub keeps the entire relationship — first form fill, all follow-up messages, the appointment, the outcome — in one place. AI only drafts; you always send." },
  { q: "What happens if I miss a message?",
    a: "If an inbound message goes unanswered for 4 hours, we email the owner a friendly reminder so nothing slips through the cracks." },
  { q: "Is my data siloed from other businesses?",
    a: "Yes. Every contact, message, and outcome is tenant-scoped. Nothing is ever shared or visible across businesses." },
  { q: "Can I still call and message customers directly?",
    a: "Absolutely — we do not replace your phone or email. Any conversation you have elsewhere can be logged back here so the timeline stays complete." },
];

export default function ConversationHubLanding() {
  useEffect(() => {
    document.title = "Conversation Hub — WebsiteTech.pro";
  }, []);
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <div className="min-h-screen bg-white" data-testid="conversation-hub-landing">
      <Navbar />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-14 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold inline-flex items-center gap-1">
              <MessageSquare className="w-3.5 h-3.5" /> Conversation Hub
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mt-3">
              Every website enquiry, <span className="text-orange-500">answered</span>.
            </h1>
            <p className="text-lg text-slate-600 mt-4 leading-relaxed max-w-xl">
              One inbox for forms and chat, one timeline per customer, and a
              4-hour nudge if you slip. AI helps you reply faster — you always
              stay in control.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/hub/inbox"
                     className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-3 text-sm shadow-lg"
                     data-testid="hub-cta-open-inbox">
                Open my inbox <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/lead-generation/audit"
                     className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-slate-50 ring-1 ring-slate-200 text-slate-800 font-semibold px-6 py-3 text-sm">
                <Play className="w-4 h-4" /> See a live demo
              </Link>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              No credit card. No onboarding fee. Cancel anytime.
            </p>
          </div>
          <div className="relative rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 p-6 shadow-2xl ring-1 ring-white/5">
            <div className="rounded-2xl bg-white shadow-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 font-bold">A</div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Alex Dhawan</div>
                  <div className="text-[11px] text-slate-500">form · testco.com · just now</div>
                </div>
                <span className="ml-auto text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full ring-1 bg-sky-50 text-sky-700 ring-sky-200">new</span>
              </div>
              <div className="rounded-xl bg-slate-50 ring-1 ring-slate-100 p-3 text-sm text-slate-800">
                Need a redesign for our clinic site — WordPress currently.
              </div>
              <div className="mt-3 rounded-xl bg-indigo-50 text-indigo-800 ring-1 ring-indigo-100 px-3 py-2 text-xs italic">
                AI summary: Alex wants a redesign of their clinic's existing WordPress website.
              </div>
              <div className="mt-3 text-xs font-semibold text-slate-500 uppercase tracking-widest">Draft with AI</div>
              <div className="mt-1 rounded-xl ring-1 ring-slate-200 p-3 text-sm text-slate-800 leading-snug">
                Hi Alex, thanks for reaching out! We do plenty of clinic-focused redesigns —
                would a quick 15-minute call this week work to show you what a modern
                version could look like?
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capability strip */}
      <HubCapabilityStrip />

      {/* Live demo section */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">See it in action</div>
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 mt-2">
            From "just filled the form" to "booked call" — in one screen.
          </h2>
          <p className="text-slate-600 mt-3">
            The Conversation Hub keeps every enquiry visible, drafts your first reply,
            and reminds you the moment things go quiet.
          </p>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { step: "1", title: "Enquiry lands", body: "Your website form or widget-chat pushes a message straight into the shared inbox." },
            { step: "2", title: "AI drafts a reply", body: "Click 'Draft with AI' and get a warm, on-brand starter reply — edit before you hit send." },
            { step: "3", title: "Timeline closes the loop", body: "Every message, appointment, and outcome stays on the same contact record. Ready for next time." },
          ].map((s) => (
            <div key={s.step}
                  className="rounded-2xl bg-slate-50 ring-1 ring-slate-100 p-5">
              <div className="w-8 h-8 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center justify-center">
                {s.step}
              </div>
              <div className="font-bold text-slate-900 mt-3">{s.title}</div>
              <p className="text-sm text-slate-600 mt-1 leading-snug">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 pb-20">
        <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold text-center">Answers</div>
        <h2 className="text-3xl font-bold text-slate-900 text-center mt-2">
          Frequently asked
        </h2>
        <div className="mt-6 space-y-2" data-testid="hub-faq">
          {FAQS.map((f, i) => (
            <div key={i} className="rounded-xl ring-1 ring-slate-200 bg-white overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                       className="w-full flex items-center justify-between text-left px-4 py-3 hover:bg-slate-50"
                       data-testid={`hub-faq-toggle-${i}`}>
                <span className="font-semibold text-slate-900 text-sm">{f.q}</span>
                {openFaq === i ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {openFaq === i && (
                <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}


export function HubCapabilityStrip({ className = "" }) {
  return (
    <section className={`max-w-6xl mx-auto px-4 py-10 ${className}`}
              data-testid="hub-capability-strip">
      <div className="text-center max-w-2xl mx-auto mb-8">
        <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">One system, six jobs</div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 mt-2">
          From first message to lasting customer.
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {HUB_CAPABILITIES.map(({ key, Icon, title, line }) => (
          <div key={key}
                className="group rounded-2xl bg-white ring-1 ring-slate-200 p-5 hover:ring-orange-300 hover:shadow-lg transition-all"
                data-testid={`hub-capability-${key}`}>
            <div className="w-11 h-11 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center group-hover:bg-orange-100 transition-colors">
              <Icon className="w-5 h-5" />
            </div>
            <div className="mt-3 font-bold text-slate-900">{title}</div>
            <p className="text-sm text-slate-600 mt-1 leading-snug">{line}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
